import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

// The project workspace sections, mapped to the tabs that already exist on the project page.
export const PROJECT_SECTIONS = [
  ["Overview", "Project Overview"],
  ["Selections", "Products / Sourcing"],
  ["Presentations", "Presentations"],
  ["Budget", "Budget & Financials"],
  ["Files", "Files"],
  ["Calendar", "Tasks & Timeline"],
  ["Client Portal", "Client Portal"],
];

const crumbLink = "whitespace-nowrap text-stone transition-colors hover:text-ink";

/**
 * Breadcrumbs plus the project section bar. `trail` items are {label, to}; the last
 * entry renders as plain text. `onLeave` lets a dirty form confirm before navigating.
 */
export default function ProjectNav({
  projectId,
  projectName,
  trail = [],
  activeSection,
  staffOnlySections = true,
  hideSections = false,
  onLeave,
}) {
  const guard = (e) => {
    if (onLeave && onLeave(e) === false) e.preventDefault();
  };
  const items = [
    { label: "Projects", to: "/projects" },
    ...(projectId ? [{ label: projectName || "Project", to: `/projects/${projectId}` }] : []),
    ...trail,
  ];
  const sections = staffOnlySections
    ? PROJECT_SECTIONS
    : PROJECT_SECTIONS.filter(([key]) => !["Budget", "Client Portal"].includes(key));

  return (
    <nav className="space-y-4" data-testid="project-nav" aria-label="Project navigation">
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs uppercase tracking-[0.12em]">
        {items.map((item, i) => {
          const last = i === items.length - 1;
          return (
            <li key={`${item.label}-${i}`} className="flex items-center gap-2">
              {i > 0 && <ChevronRight className="h-3 w-3 text-line" strokeWidth={2} />}
              {last || !item.to ? (
                <span data-testid="breadcrumb-current" className="max-w-[16rem] truncate text-ink">
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.to}
                  onClick={guard}
                  data-testid={`breadcrumb-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`${crumbLink} max-w-[14rem] truncate`}
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>

      {projectId && !hideSections && (
        <div className="flex gap-6 overflow-x-auto border-b border-line pb-3 scroll-thin" data-testid="project-section-bar">
          {sections.map(([key, label]) => (
            <Link
              key={key}
              to={`/projects/${projectId}?tab=${encodeURIComponent(key)}`}
              onClick={guard}
              data-testid={`section-link-${key.toLowerCase().replace(/\s/g, "-")}`}
              className={`whitespace-nowrap text-sm transition-colors ${
                activeSection === key ? "font-medium text-ink" : "text-stone hover:text-ink"
              }`}
            >
              {label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
}
